import React, { useRef } from "react";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";
import { useQuery } from "@tanstack/react-query";
import { Loader } from "rsuite";
import { useSelector } from "react-redux";
import { axInst } from "../Login/Service"; // adjust path if needed

// ================= API FUNCTION =================
const api = async (body, endpoint) => {
  const res = await axInst.post(endpoint, body);
  return res.data;
};

export default function DashboardCharts({ endpoint, Heading }) {
  const { data: filters, submitted } = useSelector(
    (e) => e.filtersDealerReview,
  );

  const chartRef = useRef(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: [Heading, submitted, endpoint],
    queryFn: () => api(filters, endpoint),
  });

  if (isLoading) {
    return (
      <div className="h-[300px] flex items-center justify-center">
        <Loader size="md" />
      </div>
    );
  }

  if (isError || !Array.isArray(data)) {
    return (
      <div className="h-[300px] flex items-center justify-center">
        <p>Data not available</p>
      </div>
    );
  }

  const apiData = data;

  // ================= BOX 1: Dynamic Months =================
  const monthKeys =
    apiData.length > 0
      ? Object.keys(apiData[0].month_performance)
          .filter((k) => k.toLowerCase() !== "variance") // remove variance
          .slice(-2) // keep last 2 months
      : [];

  const monthBarChart = {
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "none",
      },
      position: (point) => [point[0] + 20, point[1]],
    },
    legend: {
      bottom: 2,
      left: "center",
      orient: "horizontal",
      width: "100%",
      itemGap: 20,
    },
    grid: {
      top: "10%",
      left: "-5%",
      right: "5%",
      bottom: "15%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: apiData.map((d) => d.LOB),
      splitLine: { show: false },
      axisLabel: {
        show: true,
        fontSize: 10,
        interval: 0,
        rotate: 0,
        color: "#333",
      },
    },
    yAxis: { type: "value", splitLine: { show: false }, show: false },
    series: monthKeys.map((month, idx) => ({
      name: month,
      type: "bar",
      barMinHeight: 30,
      data: apiData.map((d) => d.month_performance[month]),
      label: {
        show: true,
        position: "inside", // center inside the bar
        formatter: "{c}",
        fontSize: 10,
        rotate: 90,
        color: "#fff", // use "#fff" if bar color is dark
        fontWeight: "bold",
      },
      itemStyle: {
        borderRadius: [5, 5, 0, 0],
        color: new echarts.graphic.LinearGradient(
          0,
          0,
          0,
          1, // vertical gradient
          [
            {
              offset: 0,
              color: idx % 2 === 0 ? "#FFEF5E" : "#68DBF2",
            },
            {
              offset: 1,
              color: idx % 2 === 0 ? "#F7936F" : "#509CF5",
            },
          ],
        ),
      },
    })),
  };

  const monthVariancePie = {
    title: {
      text: "Variance",
      top: "10%",
      left: "center",
      textStyle: {
        fontFamily: "Uni Neue",
        fontSize: 14,
        fontWeight: "600",
      },
    },
    tooltip: {
      trigger: "item",
      formatter: (params) => `${params.name}: ${params.data.actualValue}%`,
    },
    legend: {
      top: "5%",
      left: "center",
      show: false,
    },
    series: [
      {
        name: "Month Variance",
        type: "pie",
        radius: ["35%", "75%"], // donut
        minAngle: 50,
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10, // rounded slices
          borderColor: "#fff", // white gap
          borderWidth: 2,
        },
        label: {
          show: true,
          position: "inside",
          formatter: (params) => `${params.name}\n${params.data.actualValue}%`,
          fontSize: 8,
          color: "#fff",
          lineHeight: 12,
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 11,
            fontWeight: "bold",
          },
        },
        labelLine: {
          show: false,
        },
        data: apiData.map((d) => ({
          name: d.LOB,
          value: Math.abs(d.month_performance.variance),
          actualValue: d.month_performance.variance,
        })),
      },
    ],
  };

  // ================= BOX 2: Dynamic YTD =================
  // ----------------- KEYS -----------------
  const ytdKeys = apiData.length
    ? Object.keys(apiData[0].ytd_performance).filter(
        (k) =>
          !k.toLowerCase().includes("exit") &&
          !k.toLowerCase().includes("variance"),
      )
    : [];

  const exitKeys = apiData.length
    ? Object.keys(apiData[0].ytd_performance).filter((k) =>
        k.toLowerCase().includes("exit"),
      )
    : [];

  // ----------------- COLORS -----------------
  const gradients = [
    ["#FFEF5E", "#F7936F"],
    ["#68DBF2", "#509CF5"],
    ["#A0A0A0", "#C6BFC9"],
  ];

  // ----------------- HELPERS -----------------
  const MIN_STACK_VALUE = 50;

  const applyMinValue = (val) =>
    val > 0 && val < MIN_STACK_VALUE ? MIN_STACK_VALUE : val;

  // ----------------- MAX STACK CALCULATION (🔥 FIX) -----------------
  const maxStackValue = Math.max(
    ...apiData.map((row) => {
      const fySum = ytdKeys.reduce(
        (sum, k) => sum + (row.ytd_performance[k] || 0),
        0,
      );

      const exitSum = exitKeys.reduce(
        (sum, k) => sum + (row.ytd_performance[k] || 0),
        0,
      );

      return Math.max(fySum, exitSum);
    }),
  );

  // ----------------- ECHART OPTION -----------------
  const ytdHorizontalBar = {
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (params) => {
        let text = `${params[0].name}<br/>`;
        params.forEach((item) => {
          const original =
            apiData[item.dataIndex]?.ytd_performance[item.seriesName];
          text += `${item.seriesName}: ${original}<br/>`;
        });
        return text;
      },
    },

    legend: {
      bottom: 2,
      left: "center",
      orient: "horizontal",
      itemGap: 5,
      textStyle: { fontSize: 11 },
    },

    grid: {
      top: "8%",
      left: "2%",
      right: "8%",
      bottom: "18%",
      containLabel: true,
    },

    yAxis: {
      type: "category",
      data: apiData.map((d) => d.LOB),
    },

    xAxis: {
      type: "value",
      splitLine: { show: false },
      axisLabel: { show: false },
      max: maxStackValue * 1.15, // ✅ NO OVERFLOW
    },

    series: [
      // ----------------- FY STACK -----------------
      ...ytdKeys.map((key, idx) => {
        const [light, dark] = gradients[idx % gradients.length];

        return {
          name: key,
          type: "bar",
          stack: "FY",
          data: apiData.map((d) => applyMinValue(d.ytd_performance[key])),
          label: {
            show: true,
            position: "inside",
            formatter: (p) => apiData[p.dataIndex].ytd_performance[key],
            fontSize: 10,
            color: "#fff",
            fontWeight: "bold",
          },
          itemStyle: {
            borderRadius: idx === ytdKeys.length - 1 ? [0, 6, 6, 0] : 0,
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0, color: light },
              { offset: 1, color: dark },
            ]),
          },
        };
      }),

      // ----------------- EXIT STACK -----------------
      ...exitKeys.map((key, idx) => {
        const [light, dark] =
          gradients[(idx + ytdKeys.length) % gradients.length];

        return {
          name: key,
          type: "bar",
          stack: "EXIT",
          barMinHeight: 30,
          barGap: "30%", // ✅ visual separation
          data: apiData.map((d) => applyMinValue(d.ytd_performance[key])),
          label: {
            show: true,
            position: "inside",
            formatter: (p) => apiData[p.dataIndex].ytd_performance[key],
            fontSize: 10,
            color: "#fff",
            fontWeight: "bold",
          },
          itemStyle: {
            borderRadius: [0, 6, 6, 0],
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0, color: light },
              { offset: 1, color: dark },
            ]),
          },
        };
      }),
    ],
  };

  const ytdVariancePie = {
    title: {
      text: "Variance",
      top: "10%",
      left: "center",
      textStyle: {
        fontFamily: "Uni Neue",
        fontSize: 14,
        fontWeight: "600",
      },
    },
    tooltip: {
      trigger: "item",
      formatter: (params) => `${params.name}: ${params.data.actualValue}%`,
    },
    legend: {
      top: "5%",
      left: "center",
      show: false,
    },
    series: [
      {
        name: "YTD Variance",
        type: "pie",
        radius: ["35%", "80%"], // donut
        minAngle: 50,
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: "#fff",
          borderWidth: 2,
        },
        label: {
          show: true, // 👈 labels ON
          position: "inside",
          formatter: (params) => `${params.name}\n${params.data.actualValue}%`,
          fontSize: 8,
          color: "#fff",
          lineHeight: 12,
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 11,
            fontWeight: "bold",
          },
        },
        labelLine: {
          show: false,
        },
        data: apiData.map((d) => ({
          name: d.LOB,
          value: Math.abs(d.ytd_performance.variance),
          actualValue: d.ytd_performance.variance,
        })),
      },
    ],
  };

  // ================= BOX 3: Quarterly (unchanged) =================
  const quarterlyLineChart = {
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "none",
      },
    },

    legend: {
      bottom: 3,
      left: "center",
      itemGap: 15,
      textStyle: { fontSize: 11 },
    },

    grid: {
      top: "10%",
      left: "3%",
      right: "6%",
      bottom: "15%",
      containLabel: true,
    },

    xAxis: {
      type: "category",
      data: ["Q1", "Q2", "Q3", "Q4"],
      splitLine: { show: false },
    },

    yAxis: {
      type: "value",
      splitLine: { show: false },
    },

    series: apiData.map((d) => ({
      name: d.LOB,
      type: "line",
      data: Object.values(d.quarterly_performance),
      label: { show: false }, // 👈 default OFF
    })),
  };

  // 🔹 Legend interaction handler
  const onLegendChange = (params) => {
    const selected = params.selected;
    const chart = chartRef.current?.getEchartsInstance();
    if (!chart) return;

    const active = Object.keys(selected).filter((k) => selected[k]);
    const total = Object.keys(selected).length;

    const showLabels = active.length < total; // Only when not all selected

    const currentOption = chart.getOption();

    chart.setOption({
      series: currentOption.series.map((s) => ({
        name: s.name,
        label: {
          show: showLabels && selected[s.name],
          position: "top",
          distance: 5, // gap from the point
          fontSize: 10,
          formatter: "{c}",
        },
      })),

      // optional: automatic label layout to prevent overlap
      labelLayout: {
        hideOverlap: true,
      },
    });
  };

  // ===================== RENDER =====================
  return (
    <div className="bg-white shadow-lg text-xs rounded-lg w-full relative h-full mt-2 md:mt-0 mb-2 p-2 ">
      <h2 className="text-lg font-bold text-gray-800  mb-2">{Heading}</h2>
      <div className="grid md:grid-cols-3 grid-col-1 gap-2 w-full">
        {/* BOX 1: Month Performance */}
        <div className="border rounded-xl bg-white">
          <div className="text-center bg-[#2A5775] text-white font-semibold text-lg mb-1">
            Month Performance
          </div>
          <div className="grid grid-cols-[minmax(0,55%)_minmax(0,45%)] gap-[12px]">
            <div>
              <ReactECharts
                option={monthBarChart}
                style={{ height: 250, width: 250 }}
              />
            </div>
            <ReactECharts option={monthVariancePie} style={{ height: 250 }} />
          </div>
        </div>

        {/* BOX 2: YTD Performance */}
        <div className="border rounded-xl bg-white">
          <div className="text-center bg-[#2A5775] text-white font-semibold text-lg mb-1">
            YTD Performance
          </div>
          <div className="grid grid-cols-[minmax(0,55%)_minmax(0,45%)] gap-[10px]">
            <ReactECharts
              option={ytdHorizontalBar}
              style={{ height: 250, width: 280 }}
            />
            <ReactECharts option={ytdVariancePie} style={{ height: 250 }} />
          </div>
        </div>

        {/* BOX 3: Quarterly Performance */}
        <div className="border rounded-xl bg-white">
          <div className="text-center bg-[#2A5775] text-white font-semibold text-lg mb-1">
            Quarterly Performance
          </div>
          <ReactECharts
            ref={chartRef}
            option={quarterlyLineChart}
            style={{ height: 250 }}
            onEvents={{
              legendselectchanged: onLegendChange,
            }}
          />
        </div>
      </div>
    </div>
  );
}
